import { AutoHeight, Button, Checkbox } from "~/components";
import { LinesStepProps, SelectableLine } from "./interfaces";
import cn from "classnames";

const LinesStep = <Line extends SelectableLine>({
    title,
    emptyText,
    phases,
    usedCount,
    selection,
    renderMeta,
    loading,
    createLabel,
    onClose,
    onCreate,
}: LinesStepProps<Line>) => {
    const { isPhaseOn, isLineOn, togglePhase, toggleLine } = selection;
    const hasSelection = phases.some(
        ({ phase, lines }) =>
            isPhaseOn(phase.id) && lines.some((line) => isLineOn(line.id)),
    );

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <h3 className="mb-4 font-bold">{title}</h3>
            <div className="min-h-0 flex-1 overflow-y-overlay pr-1">
                <div className="flex flex-col gap-y-4">
                    {phases.length === 0 && (
                        <p className="text-gray-500">
                            {usedCount > 0
                                ? "Everything in this budget has been created already"
                                : emptyText}
                        </p>
                    )}
                    {phases.map(({ phase, lines }) => (
                        <div key={phase.id}>
                            <div className="mb-2 flex items-center gap-x-2 text-sm">
                                <Checkbox
                                    checked={isPhaseOn(phase.id)}
                                    onClick={({ currentTarget }) =>
                                        togglePhase(
                                            phase.id,
                                            currentTarget.checked,
                                        )
                                    }
                                    className={cn(
                                        isPhaseOn(phase.id) && "font-bold",
                                        !isPhaseOn(phase.id) &&
                                            "text-gray-500 line-through",
                                    )}
                                    label={
                                        phase.data.title ?? "(untitled phase)"
                                    }
                                />
                            </div>
                            <AutoHeight
                                manual
                                expanded={isPhaseOn(phase.id)}
                                className="ml-4 flex flex-col gap-y-2"
                            >
                                {lines.map((line) => (
                                    <div
                                        key={line.id}
                                        className="flex items-center justify-between gap-x-2 text-sm"
                                    >
                                        <Checkbox
                                            checked={isLineOn(line.id)}
                                            onClick={({ currentTarget }) =>
                                                toggleLine(
                                                    line.id,
                                                    currentTarget.checked,
                                                )
                                            }
                                            className={cn(
                                                !isLineOn(line.id) &&
                                                    "text-gray-500 line-through",
                                            )}
                                            label={
                                                line.data.description ||
                                                "(no description)"
                                            }
                                        />
                                        {renderMeta(line)}
                                    </div>
                                ))}
                            </AutoHeight>
                        </div>
                    ))}
                    {usedCount > 0 && phases.length > 0 && (
                        <p className="text-xs text-gray-400">
                            {usedCount} line(s) already created from this budget
                            are hidden
                        </p>
                    )}
                </div>
            </div>
            <div className="mt-4 flex shrink-0 justify-between border-t border-gray-200 pt-4">
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    disabled={loading || (phases.length > 0 && !hasSelection)}
                    onClick={onCreate}
                >
                    {createLabel}
                </Button>
            </div>
        </div>
    );
};

export default LinesStep;
